import { TestTypes } from '@hawkindynamics/core-client';
import dayjs from 'dayjs';
import {
  CollectionReference,
  DocumentData,
  Firestore,
  Query,
  QueryConstraint,
  QueryDocumentSnapshot,
  QuerySnapshot,
  collection,
  doc,
  startAfter as fsStartAfter,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import chunk from 'lodash/chunk';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';
import { collectionData, docData } from 'rxfire/firestore';
import { EMPTY, Observable, combineLatest, from } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { deidentifyAthlete } from '../../utils/deidentifyAthlete';
import { queryHashGenerator } from '../../utils/queryHash';
import { mergeMetrics } from '../../utils/testMetrics';
import {
  EntityType,
  IAthlete,
  IEntityMap,
  IMappable,
  IProvidedData,
  IRange,
  IScoreboardSettings,
  ISelectableTag,
  ITag,
  ITest,
  ITestType,
  IUnmergedTest,
  IUser,
} from '../types';
import { IEntities, entitiesProvider } from './entitiesProvider';
import { tagsProvider } from './tagsProvider';
import { testTypesProvider } from './testTypesProvider';
import { authenticatedUserProvider } from './userProvider';

/**
 * @description Today's tests
 */
export const activity = (): Observable<ITest[]> => {
  // prettier-ignore
  const today = dayjs().startOf('day').unix() * 1000;
  return authenticatedUserProvider().pipe(
    switchMap((user: IUser): Observable<IUnmergedTest[]> => {
      if (isEmpty(user)) return EMPTY;

      if (!user.claims.orgAdmin) {
        if (user.teams.length <= 10) {
          return collectionData<IUnmergedTest>(
            query(
              collection(
                user.services.firestore,
                `orgs/${user.orgId}/tests`
              ) as CollectionReference<IUnmergedTest>,
              where('active', '==', true),
              where('timestamp', '>=', today),
              where('teams', 'array-contains-any', user.teams),
              orderBy('timestamp', 'desc')
            ),
            { idField: 'id' }
          );
        } else {
          const batches = chunk(user.teams, 10);
          const queries = batches.map(
            (batch: string[]): Observable<IUnmergedTest[]> => {
              return collectionData<IUnmergedTest>(
                query(
                  collection(
                    user.services.firestore,
                    `orgs/${user.orgId}/tests`
                  ) as CollectionReference<IUnmergedTest>,
                  where('active', '==', true),
                  where('timestamp', '>=', today),
                  where('teams', 'array-contains-any', batch),
                  orderBy('timestamp', 'desc')
                ),
                { idField: 'id' }
              );
            }
          );
          return combineLatest(queries).pipe(
            map((result: IUnmergedTest[][]): IUnmergedTest[] => flatten(result))
          );
        }
      } else {
        return collectionData<IUnmergedTest>(
          query(
            collection(
              user.services.firestore,
              `orgs/${user.orgId}/tests`
            ) as CollectionReference<IUnmergedTest>,
            where('active', '==', true),
            where('timestamp', '>=', today),
            orderBy('timestamp', 'desc')
          ),
          { idField: 'id' }
        );
      }
    }),
    switchMap(
      (tests: IUnmergedTest[]): Observable<ITest[]> => mergeTests(tests, false)
    ),
    switchMap((tests: ITest[]): Observable<ITest[]> => mergeMetrics(tests))
  );
};

/**
 * @description merges athlete, tags, and test type with a collection of tests from the database
 */
const mergeTests = (
  tests: IUnmergedTest[],
  deidentify?: boolean
): Observable<ITest[]> => {
  const athletes$ = entitiesProvider().pipe(
    map(
      (entities: IEntities): IEntityMap =>
        toMap(entities.athletes as IMappable[])
    )
  );

  return combineLatest([tagsProvider(), testTypesProvider(), athletes$]).pipe(
    map((result: [ITag[], ITestType[], IEntityMap]): ITest[] => {
      const [tags, testTypes, athletes] = result;

      return tests
        .map((test: IUnmergedTest): ITest => {
          const athlete = athletes[test.athleteId] as IAthlete;

          const newTest: ITest = {
            id: test.id,
            ...test,
            testType: testTypes.find(
              (type: ITestType): boolean => type.id === test.testType
            ),
            tags: tags.filter((tag: ITag): boolean =>
              test.tags.includes(tag.id)
            ),
            athlete:
              deidentify === true && athlete
                ? deidentifyAthlete(athlete)
                : athlete,
          };
          return newTest;
        })
        .filter((mTest: ITest): boolean => !!mTest.athlete);
    })
  );
};

/**
 * @description converts an array of entities into a map keyed by the entity id for quick lookups
 * @param {IMappable[]} items an array of `mappable` (contains id property) entities
 * @returns {IEntityMap}
 */
const toMap = (items: IMappable[]): IEntityMap => {
  return items.reduce((acc: IEntityMap, curr: IMappable): IEntityMap => {
    acc[curr.id] = curr;
    return acc;
  }, {});
};

export interface IInifiniteTestQuery {
  entityId: string;
  entityType: EntityType;
  includeInactiveMetrics?: boolean;
  startAfter?: number;
  range?: IRange;
  facet?: string;
  deidentify?: boolean;
  showDisabledTests?: boolean;
  sessionId?: number;
}
/**
 * @description fetchs tests in batches for infinite scroll
 * @param startAfter
 */
export const infiniteTests = async (
  args: IInifiniteTestQuery
): Promise<ITest[]> => {
  const {
    entityId,
    entityType,
    startAfter,
    range,
    facet,
    deidentify,
    showDisabledTests,
  } = args;
  const tests$ = authenticatedUserProvider().pipe(
    switchMap((user: IUser): Observable<ITest[]> => {
      const terminalCollection = facet ? `tests_type_${facet}` : 'tests';
      const path = `orgs/${user.orgId}/${entityType}/${entityId}/${terminalCollection}`;
      const mQuery: Query = infiniteQueryBuilder({
        firestore: user.services.firestore,
        path,
        startAfter,
        range,
        showDisabledTests,
      });

      return from(
        getDocs(mQuery).then((snap: QuerySnapshot): IUnmergedTest[] =>
          unpackSnapshot<IUnmergedTest>(snap)
        )
      ).pipe(
        switchMap(
          (tests: IUnmergedTest[]): Observable<ITest[]> =>
            mergeTests(tests, deidentify)
        ),
        switchMap((tests: ITest[]): Observable<ITest[]> => mergeMetrics(tests))
      );
    })
  );
  return tests$.pipe(take(1)).toPromise();
};

interface IQueryBuilderArgs {
  firestore: Firestore;
  path: string;
  startAfter?: number;
  range?: IRange;
  showDisabledTests?: boolean;
}

/**
 * @description builds inifinite query from arguments
 * @param args
 * @returns
 */
const infiniteQueryBuilder = (args: IQueryBuilderArgs): Query<DocumentData> => {
  const {
    firestore: mFirestore,
    path,
    startAfter,
    range,
    showDisabledTests,
  } = args;

  if (!isEmpty(range)) {
    if (!startAfter) {
      return query(
        collection(mFirestore, path),
        where('active', '==', showDisabledTests ? false : true),
        where('timestamp', '>=', range.from.value),
        where('timestamp', '<', range.to.value),
        orderBy('timestamp', 'desc'),
        limit(100)
      );
    } else {
      return query(
        collection(mFirestore, path),
        where('active', '==', showDisabledTests ? false : true),
        where('timestamp', '>=', range.from.value),
        where('timestamp', '<', range.to.value),
        orderBy('timestamp', 'desc'),
        fsStartAfter(startAfter),
        limit(100)
      );
    }
  } else {
    if (!startAfter) {
      return query(
        collection(mFirestore, path),
        where('active', '==', showDisabledTests ? false : true),
        orderBy('timestamp', 'desc'),
        limit(100)
      );
    } else {
      return query(
        collection(mFirestore, path),
        where('active', '==', showDisabledTests ? false : true),
        orderBy('timestamp', 'desc'),
        fsStartAfter(startAfter),
        limit(100)
      );
    }
  }
};

/**
 * @description query all the tests
 * @param args
 * @returns
 */
export const exportTests = (args: IInifiniteTestQuery): Observable<ITest[]> => {
  const {
    entityId,
    entityType,
    range,
    includeInactiveMetrics,
    deidentify,
    sessionId,
    facet,
  } = args;

  const tests$ = authenticatedUserProvider().pipe(
    switchMap((user: IUser): Observable<ITest[]> => {
      const terminalCollection = facet
        ? `tests_type_${TestTypes.resolveTestTypeId(facet)}`
        : 'tests';
      const databasePath = `orgs/${user.orgId}/${entityType}/${entityId}/${terminalCollection}`;

      let mQuery: Query;

      if (Boolean(sessionId)) {
        mQuery = query(
          collection(user.services.firestore, databasePath),
          where('active', '==', true),
          where('sessionId', '==', sessionId),
          orderBy('timestamp', 'desc')
        );
      } else if (isEmpty(range)) {
        mQuery = query(
          collection(user.services.firestore, databasePath),
          where('active', '==', true),
          orderBy('timestamp', 'desc')
        );
      } else {
        mQuery = query(
          collection(user.services.firestore, databasePath),
          where('active', '==', true),
          where('timestamp', '>=', range?.from?.value),
          where('timestamp', '<=', range?.to?.value),
          orderBy('timestamp', 'desc')
        );
      }

      return from(
        getDocs(mQuery).then((snap: QuerySnapshot): IUnmergedTest[] =>
          unpackSnapshot<IUnmergedTest>(snap)
        )
      ).pipe(
        switchMap(
          (tests: IUnmergedTest[]): Observable<ITest[]> =>
            mergeTests(tests, deidentify)
        ),
        switchMap(
          (tests: ITest[]): Observable<ITest[]> =>
            mergeMetrics(tests, includeInactiveMetrics)
        )
      );
    })
  );
  return tests$.pipe(take(1));
};

const unpackSnapshot = <T>(snap: QuerySnapshot): T[] => {
  return snap.docs.map((doc: QueryDocumentSnapshot): T => doc.data() as T);
};

interface ITestObservationArgs {
  queryHash: string;
  range: IRange;
  ignoreTimeframeConstraint?: boolean;
  deidentify?: boolean;
}
interface ITestQueryArgs extends ITestObservationArgs {
  user: IUser;
  firestore: Firestore;
}

/**
 * @description listens to all the tests, used for scoreboard until we have a better solution
 */
const observeTests = (args: ITestObservationArgs): Observable<ITest[]> => {
  const { queryHash, ignoreTimeframeConstraint, range, deidentify } = args;
  return authenticatedUserProvider().pipe(
    switchMap((user: IUser): Observable<IUnmergedTest[]> => {
      const queryArgs = {
        user,
        queryHash,
        range,
        ignoreTimeframeConstraint,
        firestore: user.services.firestore,
      };
      return user.claims.orgAdmin
        ? observeTestsAsOrgAdmin(queryArgs)
        : observeTestsAsNonOrgAdmin(queryArgs);
    }),
    switchMap(
      (tests: IUnmergedTest[]): Observable<ITest[]> =>
        mergeTests(tests, deidentify)
    ),
    switchMap((tests: ITest[]): Observable<ITest[]> => mergeMetrics(tests))
  );
};

/**
 * @description listens to tests as an org admin
 * @param orgId
 * @param queryHash
 * @param range
 * @returns
 */
const observeTestsAsOrgAdmin = (
  args: ITestQueryArgs
): Observable<IUnmergedTest[]> => {
  const { user, queryHash, range, ignoreTimeframeConstraint, firestore } = args;
  if (ignoreTimeframeConstraint) {
    return collectionData(
      query(
        collection(
          firestore,
          `orgs/${user.orgId}/tests`
        ) as CollectionReference<IUnmergedTest>,
        where('queryHash', '==', queryHash),
        where('active', '==', true),
        where('timestamp', '>=', range.from.value),
        orderBy('timestamp', 'desc')
      ),
      { idField: 'id' }
    );
  } else {
    return collectionData(
      query(
        collection(
          firestore,
          `orgs/${user.orgId}/tests`
        ) as CollectionReference<IUnmergedTest>,
        where('queryHash', '==', queryHash),
        where('active', '==', true),
        where('timestamp', '>=', range.from.value),
        where('timestamp', '<=', range.to.value),
        orderBy('timestamp', 'desc')
      ),
      { idField: 'id' }
    );
  }
};

/**
 * @description listens to tests as a non org admin
 * @param user
 * @param queryHash
 * @param range
 * @param ignoreToConstraint
 * @returns
 */
const observeTestsAsNonOrgAdmin = (
  args: ITestQueryArgs
): Observable<IUnmergedTest[]> => {
  const { user, ignoreTimeframeConstraint, queryHash, range, firestore } = args;
  if (user.teams.length <= 10) {
    if (ignoreTimeframeConstraint) {
      return collectionData(
        query(
          collection(
            firestore,
            `orgs/${user.orgId}/tests`
          ) as CollectionReference<IUnmergedTest>,
          where('queryHash', '==', queryHash),
          where('active', '==', true),
          where('timestamp', '>=', range.from.value),
          orderBy('timestamp', 'desc'),
          where('teams', 'array-contains-any', user.teams)
        ),
        { idField: 'id' }
      );
    } else {
      return collectionData(
        query(
          collection(
            firestore,
            `orgs/${user.orgId}/tests`
          ) as CollectionReference<IUnmergedTest>,
          where('queryHash', '==', queryHash),
          where('active', '==', true),
          where('timestamp', '>=', range.from.value),
          where('timestamp', '<=', range.to.value),
          orderBy('timestamp', 'desc'),
          where('teams', 'array-contains-any', user.teams)
        ),
        { idField: 'id' }
      );
    }
  } else {
    const teamBatches = chunk(user.teams, 10);
    const queries = teamBatches.map(
      (batch: string[]): Observable<IUnmergedTest[]> => {
        if (ignoreTimeframeConstraint) {
          return collectionData(
            query(
              collection(
                firestore,
                `orgs/${user.orgId}/tests`
              ) as CollectionReference<IUnmergedTest>,
              where('queryHash', '==', queryHash),
              where('active', '==', true),
              where('timestamp', '>=', range.from.value),
              orderBy('timestamp', 'desc'),
              where('teams', 'array-contains-any', batch)
            ),
            { idField: 'id' }
          );
        } else {
          return collectionData(
            query(
              collection(
                firestore,
                `orgs/${user.orgId}/tests`
              ) as CollectionReference<IUnmergedTest>,
              where('queryHash', '==', queryHash),
              where('active', '==', true),
              where('timestamp', '>=', range.from.value),
              where('timestamp', '<=', range.to.value),
              orderBy('timestamp', 'desc'),
              where('teams', 'array-contains-any', batch)
            ),
            { idField: 'id' }
          );
        }
      }
    );
    return combineLatest(queries).pipe(
      map((result: IUnmergedTest[][]): IUnmergedTest[] => flatten(result))
    );
  }
};

/**
 * @description observable of a single test
 * @param orgId
 * @param testId
 * @returns
 */
export const singleTest = (
  testId: string,
  includeInactiveMetrics: boolean,
  deidentify: boolean
): Observable<ITest> => {
  return authenticatedUserProvider().pipe(
    switchMap((user: IUser): Observable<DocumentData> => {
      if (!user) return EMPTY;
      return docData(
        doc(user.services.firestore, `orgs/${user.orgId}/tests/${testId}`)
      );
    }),
    map((mDoc: DocumentData): IUnmergedTest => mDoc as IUnmergedTest),
    switchMap(
      (tests: IUnmergedTest): Observable<ITest[]> =>
        mergeTests([tests], deidentify)
    ),
    switchMap(
      (tests: ITest[]): Observable<ITest[]> =>
        mergeMetrics(tests, includeInactiveMetrics)
    ),
    map((mergedTests: ITest[]): ITest => mergedTests[0])
  );
};

export interface IScoreboardData extends IProvidedData {
  settings: IScoreboardSettings;
  data: ITest[];
}

/**
 * @description observes tests that match the provided settings.
 * When additionalQueryHashes are provided (e.g. from split-view per-column tags),
 * multiple Firestore listeners are created and results are merged.
 * @param scoreboardSettings
 * @param additionalQueryHashes extra queryHashes to fetch (e.g. per-column tag hashes)
 */
export const scoreboardTestsProvider = (
  scoreboardSettings: IScoreboardSettings,
  additionalQueryHashes?: string[]
): Observable<IScoreboardData> => {
  const primaryHash = queryHashGenerator(
    scoreboardSettings.testType.value,
    scoreboardSettings.tags.map(
      (selectableTag: ISelectableTag): string => selectableTag.value
    )
  );

  const ignoreTimeframeConstraint =
    scoreboardSettings.dateRangeType.value !== 'custom';

  const baseArgs = {
    range: scoreboardSettings.dateRange,
    ignoreTimeframeConstraint,
  };

  const allHashes = Array.from(
    new Set([primaryHash, ...(additionalQueryHashes ?? [])])
  );

  const tests$: Observable<ITest[]> =
    allHashes.length === 1
      ? observeTests({ ...baseArgs, queryHash: allHashes[0] })
      : combineLatest(
          allHashes.map(
            (hash: string): Observable<ITest[]> =>
              observeTests({ ...baseArgs, queryHash: hash })
          )
        ).pipe(
          map((results: ITest[][]): ITest[] =>
            uniqBy(flatten(results), (test: ITest): string => test.id)
          )
        );

  return tests$.pipe(
    map(
      (tests: ITest[]): IScoreboardData => ({
        settings: scoreboardSettings,
        data: tests,
      })
    )
  );
};

/**
 * @description gets the tests for a session for the entity
 * @param entityId
 * @param entityType
 * @param sessionId
 */
export const entityTestsBySession = (
  entityId: string,
  entityType: EntityType,
  sessionId: number
): Observable<ITest[]> => {
  return authenticatedUserProvider().pipe(
    switchMap((user: IUser): Observable<ITest[]> => {
      let tests;
      if (user.claims.orgAdmin) {
        tests = collectionData(
          query(
            collection(user.services.firestore, `/orgs/${user.orgId}/tests`),
            constrainToEntity(entityId, entityType),
            where('sessionId', '==', sessionId),
            where('active', '==', true),
            orderBy('timestamp', 'desc')
          ),
          { idField: 'id' }
        );
      } else {
        if (user.teams.length <= 10) {
          tests = collectionData(
            query(
              collection(user.services.firestore, `/orgs/${user.orgId}/tests`),
              where('teams', 'array-contains-any', user.teams),
              where('sessionId', '==', sessionId),
              where('active', '==', true),
              orderBy('timestamp', 'desc')
            ),
            { idField: 'id' }
          ).pipe(
            map((mTests: IUnmergedTest[]): IUnmergedTest[] =>
              filterToEntity(entityId, entityType, mTests)
            )
          );
        } else {
          const teamBatches = chunk(user.teams, 10);
          const queries = teamBatches.map(
            (batch: string[]): Observable<IUnmergedTest[]> => {
              return collectionData(
                query(
                  collection(
                    user.services.firestore,
                    `/orgs/${user.orgId}/tests`
                  ) as CollectionReference<IUnmergedTest>,
                  where('teams', 'array-contains-any', batch),
                  where('sessionId', '==', sessionId),
                  where('active', '==', true),
                  orderBy('timestamp', 'desc')
                ),
                { idField: 'id' }
              );
            }
          );
          tests = combineLatest(queries).pipe(
            map((result: IUnmergedTest[][]): IUnmergedTest[] =>
              flatten(result)
            ),
            /// filter the tests to the entity
            map((mTests: IUnmergedTest[]): IUnmergedTest[] =>
              filterToEntity(entityId, entityType, mTests)
            ),
            /// remove any duplicates
            map((mTests: IUnmergedTest[]): IUnmergedTest[] =>
              uniqBy(mTests, (test: IUnmergedTest): string => test.id)
            )
          );
        }
      }
      return tests.pipe(
        switchMap(
          (mTests: IUnmergedTest[]): Observable<ITest[]> =>
            mergeTests(mTests, false)
        ),
        switchMap(
          (mTests: ITest[]): Observable<ITest[]> => mergeMetrics(mTests)
        )
      );
    })
  );
};

/**
 * @description filters tests that belong to the supplied entity
 * @param entityId
 * @param entityType
 * @param tests
 * @returns
 */
const filterToEntity = (
  entityId: string,
  entityType: EntityType,
  tests: IUnmergedTest[]
): IUnmergedTest[] => {
  switch (entityType) {
    case 'athletes':
      return tests.filter(
        (t: IUnmergedTest): boolean => t.athleteId === entityId
      );
    case 'groups':
      return tests.filter((t: IUnmergedTest): boolean =>
        t?.groups?.includes(entityId)
      );
    case 'teams':
      return tests.filter((t: IUnmergedTest): boolean =>
        t?.teams?.includes(entityId)
      );
  }
};

/**
 * @description returns the correct query constraint for the given entity type
 * @param entityId
 * @param entityType
 * @returns
 */
const constrainToEntity = (
  entityId: string,
  entityType: EntityType
): QueryConstraint => {
  switch (entityType) {
    case 'athletes':
      return where('athleteId', '==', entityId);
    case 'groups':
      return where('groups', 'array-contains', entityId);
    case 'teams':
      return where('teams', 'array-contains', entityId);
  }
};
